import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface VendeurErp {
  id: number;
  identifiant: string;
  nom: string;
  prenom: string;
  telephone: string | null;
  station_id?: number;
  code_station?: string;
  station_nom?: string;
}

export interface CaisseVendeurErpRow {
  id: number;
  caisse_id: number;
  vendeur_id: number;
  vendeur_identifiant: string;
  vendeur_nom: string;
  vendeur_prenom: string;
  vendeur_telephone: string | null;
  vendeur_code_station?: string;
}

export interface VendeurTableRow {
  id: string;
  IDENTIFIANT: string;
  NOM: string;
  PRÉNOM: string;
  TÉLÉPHONE: string;
}

export interface CaisseVendeurLink {
  id: number;
  caisse_id: number;
  vendeur_id: number;
  numero_de_caisse?: number | null;
  entrepot_libelle?: string | null;
  vendeur_identifiant?: string;
  vendeur_nom?: string;
  vendeur_prenom?: string;
}

export interface CaisseOption {
  id: string;
  label: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseVendeurService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  listErp(): Observable<VendeurErp[]> {
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp/list`;
    return this.http.get<{ success: boolean; items?: VendeurErp[]; error?: string }>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.items)) {
          throw new Error(res?.error || 'Réponse vendeurs invalide');
        }
        return res.items;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des vendeurs.'))
      )
    );
  }

  getErp(id: number): Observable<VendeurErp> {
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp/${id}`;
    return this.http.get<{ success: boolean; item?: VendeurErp; error?: string }>(url).pipe(
      map((res) => {
        if (!res?.success || !res.item) {
          throw new Error(res?.error || 'Vendeur introuvable');
        }
        return res.item;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement du vendeur.'))
      )
    );
  }

  createErp(body: {
    identifiant: string | number;
    password: string | number;
    nom: string;
    prenom: string;
    telephone?: string | null;
  }): Observable<VendeurErp> {
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp`;
    return this.http
      .post<{ success: boolean; item?: VendeurErp; error?: string }>(url, body)
      .pipe(
        map((res) => {
          if (!res?.success || !res.item) {
            throw new Error(res?.error || 'Création impossible');
          }
          return res.item;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de la création du vendeur.'))
        )
      );
  }

  updateErp(
    id: number,
    body: Partial<{
      identifiant: string | number;
      password: string | number;
      nom: string;
      prenom: string;
      telephone: string | null;
    }>
  ): Observable<VendeurErp> {
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp/${id}`;
    return this.http.put<{ success: boolean; item?: VendeurErp; error?: string }>(url, body).pipe(
      map((res) => {
        if (!res?.success || !res.item) {
          throw new Error(res?.error || 'Mise à jour impossible');
        }
        return res.item;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour.'))
      )
    );
  }

  deleteErp(id: number): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp/${id}`;
    return this.http.delete<{ success: boolean; error?: string }>(url).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Suppression impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la suppression.'))
      )
    );
  }

  getVendeursByCaisse(caisseId: string | number): Observable<CaisseVendeurErpRow[]> {
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp/caisse/${encodeURIComponent(String(caisseId))}/vendeurs`;
    return this.http
      .get<{ success: boolean; items?: CaisseVendeurErpRow[]; error?: string }>(url)
      .pipe(
        map((res) => {
          if (!res?.success || !Array.isArray(res.items)) {
            throw new Error(res?.error || 'Réponse invalide');
          }
          return res.items;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors du chargement des vendeurs de la caisse.'))
        )
      );
  }

  linkCaisseVendeur(caisseId: number, vendeurId: number): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp/caisse-vendeur`;
    return this.http
      .post<{ success: boolean; error?: string }>(url, {
        caisse_id: caisseId,
        vendeur_id: vendeurId,
      })
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.error || 'Association impossible');
          }
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, "Erreur lors de l'association."))
        )
      );
  }

  listCaisseVendeurLinks(): Observable<CaisseVendeurLink[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp/caisse-vendeurs/station/${stationId}`;
    return this.http.get<{ success: boolean; links?: CaisseVendeurLink[]; error?: string }>(url).pipe(
      map((res) => (res?.success && Array.isArray(res.links) ? res.links : [])),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des associations.'))
      )
    );
  }

  setVendeurCaisses(vendeurId: number, caisseIds: number[]): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp/vendeur/${vendeurId}/caisses`;
    return this.http.put<{ success: boolean; error?: string }>(url, { caisse_ids: caisseIds }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Enregistrement impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, "Erreur lors de l'association vendeur / caisse."))
      )
    );
  }

  unlinkCaisseVendeur(caisseId: number, vendeurId: number): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/vendeurs/erp/caisse-vendeur/caisse/${caisseId}/vendeur/${vendeurId}`;
    return this.http.delete<{ success: boolean; error?: string }>(url).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Suppression impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du retrait du vendeur.'))
      )
    );
  }

  toTableRows(items: VendeurErp[]): VendeurTableRow[] {
    return items.map((v) => ({
      id: String(v.id),
      IDENTIFIANT: String(v.identifiant ?? ''),
      NOM: String(v.nom ?? ''),
      PRÉNOM: String(v.prenom ?? ''),
      TÉLÉPHONE:
        v.telephone != null && String(v.telephone).trim() !== '' ? String(v.telephone) : '—',
    }));
  }
}
